/*
 * Copyright (c) 2008-2019 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */
import Vue from "vue"
import { v4 as UUIDv4 } from "uuid"

/* global log */

const PAGE_SIZE = 20

const LOAD_STATES = {
  UNLOADED: "unloaded",
  LOADING: "loading",
  LOADED: "loaded",
  ERROR: "error",
}
Object.freeze(LOAD_STATES)

/**
 * Basic helper class that encapsulates the management logic of performing paged get
 * requests. Wraps both paging information as well as filter and sort information.
 */
class PagedRequestHandler {
  LoadStatus = LOAD_STATES.UNLOADED
  CurrentPage = 0
  TotalPages = 0

  #currentSearch
  #currentFilter
  #apiEndpoint
  #pageSize
  #urlFragments
  #currentCorrelationId

  constructor(apiEndpoint, pageSize = PAGE_SIZE, urlFragments = []) {
    this.#currentSearch = ""
    this.#currentFilter = {}

    this.#apiEndpoint = apiEndpoint
    this.#pageSize = pageSize
    this.#urlFragments = urlFragments
  }

  /**
   * Internal helper method to compose the unique query to be used for making a paged
   * request. Implemented generically to permit building from any set of input elements
   *
   * @param {Integer} page - index of page to get
   * @param {String} search - string to use as a search filter
   * @param {Object} filters - any combination of filter parameters, of form
   *                          {
   *                            filterName: filterValue
   *                          }
   * @returns String - to be used as query
   */
  #buildGetQuery(page, search, filters, pageSize = this.#pageSize) {
    let queryString = "?size=" + pageSize
    queryString += "&page=" + page

    if (search.length) {
      queryString += "&search=" + encodeURIComponent(`%${search?.trim()}%`)
    }

    for (let filter in filters) {
      queryString += "&" + filter + "=" + filters[filter]
    }

    return queryString
  }

  setFilter(filter) {
    this.#currentFilter = filter
    this.setCurrentPage(0)
  }

  setSearchString(search) {
    this.#currentSearch = search
    this.setCurrentPage(0)
  }

  setCurrentPage(page) {
    if (!(page === 0 && this.TotalPages === 0) && page > this.TotalPages - 1)
      throw Error("Cannot set page greater than total page count of last request")
    if (page === 0) this.LoadStatus = LOAD_STATES.UNLOADED

    this.CurrentPage = page
  }

  /**
   * Core method that wraps the primary get request and update of internal logic. Used internally
   * but also user facing to permit overriding internal logic and driving explicit query data.
   *
   * Returns a promise resolving the content of the request
   *
   * @param {Integer} page - index of page to get
   * @param {String} search - string to use as a search filter
   * @param {Object} filter - any combination of filter parameters, of form
   *                          {
   *                            filterName: filterValue
   *                          }
   * @param correlationId - a correlation id identifying the search (hash of search and filter)
   * @returns Promise
   */
  composeRequest(page = this.CurrentPage, search = "", filter = {}, correlationId) {
    this.LoadStatus = LOAD_STATES.LOADING
    return new Promise((resolve, reject) => {
      Vue.http
        .get(this.#apiEndpoint(...this.#urlFragments) + this.#buildGetQuery(page, search, filter), {
          headers: {
            "X-Correlation-ID": correlationId,
          },
        })
        .then(
          (resp) => {
            const correlationId = resp.headers.get("X-Correlation-Id")
            if (correlationId && correlationId !== this.#currentCorrelationId) {
              // Reject so we don't have to provide a result which would then be committed
              reject("Search result with id " + correlationId + " was obsolete on arrival - ignoring")
              return
            }

            this.TotalPages = resp.body.totalPages
            this.CurrentPage = resp.body.number

            this.LoadStatus = LOAD_STATES.LOADED
            resolve(resp.body.content)
          },
          (error) => {
            log.error("Failed to perform get request : ", error)

            this.LoadStatus = LOAD_STATES.ERROR
            reject(error)
          }
        )
    })
  }

  /**
   * Wrapper method for performing a request for the next page based on internal logic
   *
   * @returns Promise
   */
  retrieveNextPage() {
    let targetPage = this.CurrentPage + 1
    if (this.LoadStatus === LOAD_STATES.UNLOADED) targetPage = this.CurrentPage
    this.#currentCorrelationId = this.calcCurrentCorrelationId()
    return this.composeRequest(targetPage, this.#currentSearch, this.#currentFilter, this.#currentCorrelationId)
  }

  /**
   * Unique method permitting the retrieval of all data contained in pages from O to CurrentPage
   *
   * @returns Promise
   */
  retrieveToCurrentPage() {
    this.LoadStatus = LOAD_STATES.LOADING

    let calculatedPageSize = (this.CurrentPage + 1) * this.#pageSize
    this.#currentCorrelationId = this.calcCurrentCorrelationId()
    return new Promise((resolve, reject) => {
      Vue.http
        .get(
          this.#apiEndpoint(this.#urlFragments) +
            this.#buildGetQuery(0, this.#currentSearch, this.#currentFilter, calculatedPageSize),
          {
            headers: {
              "X-Correlation-Id": this.#currentCorrelationId,
            },
          }
        )
        .then(
          (resp) => {
            const correlationId = resp.headers.get("X-Correlation-Id")
            if (correlationId && correlationId !== this.#currentCorrelationId) {
              // Reject so we don't have to provide a result which would then be committed
              reject("Search result with id " + correlationId + " was obsolete on arrival - ignoring")
              return
            }

            // Compute the new TotalPages, this is important as it may be smaller than our stored total.
            this.TotalPages = Math.ceil(resp.body.totalElements / this.#pageSize)
            // The current page will only be updated if the total content size shrinks causing us to lose a page
            if (this.CurrentPage > this.TotalPages - 1) this.CurrentPage = this.TotalPages - 1

            this.LoadStatus = LOAD_STATES.LOADED
            resolve(resp.body.content)
          },
          (error) => {
            log.error("Failed to perform get request : ", error)

            this.LoadStatus = LOAD_STATES.ERROR
            reject(error)
          }
        )
    })
  }

  calcCurrentCorrelationId() {
    return UUIDv4()
  }
}

export { PagedRequestHandler, LOAD_STATES }
